

























































































import { Component, Vue } from 'vue-property-decorator'
import ApiClient from '@/services/api-client'
import { BusinessUnit, Company } from '@/models'
import { Utils } from '@/utils'
import UpsertBusinessUnitModal from '@/components/modals/UpsertBusinessUnitModal.vue'

@Component({
  components: { UpsertBusinessUnitModal }
})
export default class BusinessUnits extends Vue {
  private businessUnits: BusinessUnit[] = []
  private companies: Company[] = []
  private loading = false
  private searchFilter = ''
  private selectedBusinessUnit: BusinessUnit = { id: '', name: '' }

  private fields = [
    { key: 'id', label: this.$t('business_units_view.columns.id'), sortable: true },
    { key: 'name', label: this.$t('business_units_view.columns.name'), sortable: true },
    { key: 'companyId', label: this.$t('business_units_view.columns.company'), sortable: true },
    { key: 'createdOn', label: this.$t('business_units_view.columns.created_on'), sortable: true },
    { key: 'actions', label: this.$t('table.actions'), sortable: false, class: 'text-center' }
  ]

  get filteredBusinessUnits(): BusinessUnit[] {
    if (!this.searchFilter.trim()) return this.businessUnits
    const filterNormalized = Utils.normalizeText(this.searchFilter)
    return this.businessUnits.filter(bu => {
      const id = Utils.normalizeText(bu.id)
      const name = Utils.normalizeText(bu.name)
      const company = Utils.normalizeText(this.getCompanyName(bu.companyId))
      return id.includes(filterNormalized) || name.includes(filterNormalized) || company.includes(filterNormalized)
    })
  }

  async created() {
    await Promise.all([this.loadBusinessUnits(), this.loadCompanies()])
  }

  async loadBusinessUnits() {
    try {
      this.loading = true
      this.businessUnits = await ApiClient.getBusinessUnits()
    } catch (error) {
      console.error('Error loading business units:', error)
      this.$bvToast.toast(this.$t('business_units_view.error_loading').toString(), {
        title: this.$t('business_units_view.error_title').toString(),
        variant: 'danger',
        solid: true
      })
    } finally {
      this.loading = false
    }
  }

  async loadCompanies() {
    try {
      this.companies = await ApiClient.getCompanies()
    } catch (error) {
      console.error('Error loading companies:', error)
    }
  }

  getCompanyName(companyId: string | undefined): string {
    if (!companyId) return '-'
    const company = this.companies.find(c => c.id === companyId)
    return company ? company.name : companyId
  }

  openCreateModal() {
    this.selectedBusinessUnit = { id: '', name: '' }
    this.$nextTick(() => this.$bvModal.show('UpsertBusinessUnitModal'))
  }

  openEditModal(businessUnit: BusinessUnit) {
    this.selectedBusinessUnit = { ...businessUnit }
    this.$nextTick(() => this.$bvModal.show('UpsertBusinessUnitModal'))
  }

  async onBusinessUnitSaved() {
    await this.loadBusinessUnits()
    this.$bvToast.toast(this.$t('business_units_view.saved_success').toString(), {
      title: this.$t('feedback.feedback_message_success_title').toString(),
      variant: 'success',
      solid: true,
      autoHideDelay: 2000
    })
  }

  async confirmDelete(businessUnit: BusinessUnit) {
    const confirmed = await this.$bvModal.msgBoxConfirm(
      this.$t('business_units_view.confirm_delete', { name: businessUnit.name }).toString(),
      {
        title: this.$t('business_units_view.confirm_delete_title').toString(),
        okVariant: 'danger',
        okTitle: this.$t('button_actions.remove').toString(),
        cancelTitle: this.$t('button_actions.cancel').toString(),
        centered: true
      }
    )

    if (!confirmed) return

    try {
      await ApiClient.deleteBusinessUnit(businessUnit.id)
      this.$bvToast.toast(this.$t('business_units_view.deleted_success').toString(), {
        title: this.$t('feedback.feedback_message_success_title').toString(),
        variant: 'success',
        solid: true
      })
      await this.loadBusinessUnits()
    } catch (error: any) {
      console.error('Error deleting business unit:', error)
      this.$bvToast.toast(error.message || this.$t('business_units_view.error_deleting').toString(), {
        title: this.$t('business_units_view.error_title').toString(),
        variant: 'danger',
        solid: true
      })
    }
  }
}
